import React from 'react';
import { useIntl } from 'react-intl';
import { bool, shape, string } from 'prop-types';
import { Loader, User as AccountIcon } from 'react-feather';
import { useAccountChip } from '@magento/peregrine/lib/talons/AccountChip/useAccountChip';
import { useStyle } from '@magento/venia-ui/lib/classify';
import Icon from '../Icon';
import defaultClasses from './accountChip.module.css';
import { GET_CUSTOMER_DETAILS } from './accountChip.gql';
/**
 * The AccountChip component shows an icon next to some text.
 * Sometimes the text is static, sometimes it is dynamic based on the user's name,
 * and it can also be a loading icon to indicate that we're fetching the user's name.
 *
 * @param {Object} props
 * @param {Object} props.classes - CSS classes to override element styles.
 * @param {String} props.fallbackText - The text to display when the user is not signed in
 *  or when we're loading details but don't want to show a loading icon.
 * @param {Boolean} props.shouldIndicateLoading - Whether we should show a loading icon or
 *  not when the user is signed in but we don't have their details (name) yet.
 */

var AccountChip = function AccountChip(props) {
  var fallbackText = props.fallbackText,
      shouldIndicateLoading = props.shouldIndicateLoading;
  var talonProps = useAccountChip({
    queries: {
      getCustomerDetailsQuery: GET_CUSTOMER_DETAILS
    }
  });
  var currentUser = talonProps.currentUser,
      isLoadingUserName = talonProps.isLoadingUserName,
      isUserSignedIn = talonProps.isUserSignedIn;
  var classes = useStyle(defaultClasses, props.classes);

  var _useIntl = useIntl(),
      formatMessage = _useIntl.formatMessage;

  var chipText;

  if (!isUserSignedIn) {
    chipText = fallbackText;
  } else {
    if (!isLoadingUserName) {
      chipText = formatMessage({
        id: 'accountChip.chipText',
        defaultMessage: 'Hi, {name}'
      }, {
        name: currentUser.firstname
      });
    } else if (shouldIndicateLoading) {
      chipText = React.createElement(Icon, {
        classes: {
          icon: classes.loader
        },
        src: Loader
      });
    } else {
      chipText = fallbackText;
    }
  }

  return React.createElement("span", {
    className: classes.root
  }, React.createElement(Icon, {
    src: AccountIcon
  }), React.createElement("span", {
    className: classes.text
  }, chipText));
};

export default AccountChip;
AccountChip.propTypes = {
  classes: shape({
    root: string,
    loader: string,
    text: string
  }),
  fallbackText: string,
  shouldIndicateLoading: bool
};
AccountChip.defaultProps = {
  fallbackText: 'Account',
  shouldIndicateLoading: false
};